import { mapState, mapActions } from 'vuex';
import { debounce } from 'lodash';
import tabs from '@/components/generics/tabs/tabs.vue';
import orderBookOffers from '@/components/order-book/order-book-offers/order-book-offers.vue';
import orderBookPrices from '@/components/order-book/order-book-prices/order-book-prices.vue';
import orderBookShares from '@/components/order-book/order-book-shares/order-book-shares.vue';
import loaderMixin from '@/mixins/loader';
import log from '@/logger';
import ls from '@/helpers/localStorage';
import { isMarketSuspended, isMarketExpired } from '@/utils/helpers';
import { waitForFirebaseInitialized } from '@/utils/mutex';

const ORDER_BOOK_LS_KEY = 'order-book-contract-id';
const TAB_PRICES = 'prices';
const TAB_MY_SHARES = 'my-shares';
const TAB_BUY_OFFERS = 'buy-offers';
const TAB_SELL_OFFERS = 'sell-offers';

const ORDER_BOOK_DEBOUNCE_INTERVAL = 500;
const debounceRefreshOrderBook = debounce((instance) => {
  log.info('[ORDER BOOK]: api - load contracts and shares');
  const currentContractId = instance.contract.contractId;
  const localStorageContractId = ls.get(ORDER_BOOK_LS_KEY, null);

  if (!(localStorageContractId && localStorageContractId === currentContractId)) {
    log.info('[ORDER BOOK]: api - clear contracts and shares before load');
    instance.getContractShares(null);
    instance.getContractOffers(null);
  }

  // TODO: think about refactoring
  const promises = [];

  if (
    instance.contract.userOpenOrdersBuyQuantity > 0 ||
    instance.contract.userOpenOrdersSellQuantity > 0) {
    promises.push(instance.getContractOffers(instance.contract.contractId));
  }

  if (instance.contract.userQuantity > 0) {
    promises.push(instance.getContractShares(instance.contract.contractId));
  }

  if (promises.length > 0) {
    instance.loaderAdd(null, 'is-loading-notify');
    Promise.all(promises)
      .then(() => {
        // instance.tabChange(instance.defaultTab);
        ls.set(ORDER_BOOK_LS_KEY, currentContractId);
        instance.initialised = true;
        instance.loaderRemove(null, 'is-loading-notify');
      })
      .catch(() => {
        ls.set(ORDER_BOOK_LS_KEY, null);
        instance.initialised = true;
        instance.loaderRemove(null, 'is-loading-notify');
      });
  } else {
    ls.set(ORDER_BOOK_LS_KEY, currentContractId);
    instance.initialised = true;
  }
}, ORDER_BOOK_DEBOUNCE_INTERVAL);

const debounceGetOrderBook = debounce((instance) => {
  log.info('[ORDER BOOK]: api - load order book');
  instance.getOrderBook(instance.contract.contractId);
}, ORDER_BOOK_DEBOUNCE_INTERVAL);

const isPricesDisabled = market => isMarketExpired(market) || isMarketSuspended(market);


export default {
  mixins: [
    loaderMixin,
  ],
  components: {
    tabs,
    orderBookOffers,
    orderBookPrices,
    orderBookShares,
  },
  props: {
    market: {
      type: Object,
      required: true,
    },
    contract: {
      type: Object,
      required: true,
    },
    operationValue: {
      type: String,
      default: 'yes',
      validator: value => ['no', 'yes'].indexOf(value) >= 0,
    },
    operationType: {
      type: String,
      default: 'buy',
    },
    defaultTab: {
      type: String,
      default: TAB_PRICES,
    },
  },
  data() {
    const INITIALISED_TABS = [TAB_PRICES, null];
    const isInitialised = INITIALISED_TABS.includes(this.defaultTab) &&
      !isPricesDisabled(this.market);
    return {
      initialised: isInitialised,
      activeTab: (this.defaultTab || TAB_PRICES),
    };
  },
  watch: {
    initialised(value) {
      if (!value) return;
      if (this.tabs.find(tab => tab.name === this.activeTab) === undefined) {
        if (this.tabs.find(tab => tab.name === TAB_PRICES) !== undefined) {
          this.tabChange(TAB_PRICES);
        } else if (this.tabs.length > 0) {
          this.tabChange(this.tabs[0].name);
        }
      }
      this.$emit('initialised');
    },
  },
  computed: {
    ...mapState('trade', ['orderBook', 'contractShares', 'contractOffers']),
    buyOffers() {
      if (this.contract.userOpenOrdersBuyQuantity === 0) return [];
      return this.contractOffers ? this.contractOffers.filter(c => c.tradeType <= 1) : [];
    },
    sellOffers() {
      if (this.contract.userOpenOrdersSellQuantity === 0) return [];
      return this.contractOffers ? this.contractOffers.filter(c => c.tradeType > 1) : [];
    },
    tabs() {
      const tabsAvailable = [];
      if (!isPricesDisabled(this.market)) {
        tabsAvailable.push({ name: TAB_PRICES, title: 'Prices' });
      }
      if (this.contract.userQuantity > 0) {
        tabsAvailable.push({ name: TAB_MY_SHARES, title: 'My Shares' });
      }
      if (this.buyOffers && this.buyOffers.length) {
        tabsAvailable.push({ name: TAB_BUY_OFFERS, title: 'Buy Offers' });
      }
      if (this.sellOffers && this.sellOffers.length) {
        tabsAvailable.push({ name: TAB_SELL_OFFERS, title: 'Sell Offers' });
      }
      return tabsAvailable;
    },
  },
  methods: {
    ...mapActions('trade', [
      'getContractShares', 'getContractOffers',
      'updateOrderBookPeriodically', 'clearOrderBookUpdates',
      'getOrderBook', 'setTradeDefaultTab',
    ]),
    tabChange(name) {
      this.activeTab = name;
      this.setTradeDefaultTab(name);
      // if (
      //   this.activeTab === TAB_MY_SHARES &&
      //   this.contract.userQuantity > 0 &&
      //   this.contractShares.length === 0
      // ) {
      //   this.loaderAdd(null, 'is-loading-notify');
      //   this.getContractShares(this.contract.contractId)
      //     .then(() => {
      //       this.loaderRemove(null, 'is-loading-notify');
      //     })
      //     .catch(() => this.loaderRemove(null, 'is-loading-notify'));
      // }
    },
    tabVisible(name) {
      return this.orderBook && this.activeTab === name;
    },
    refreshOrderBook() {
      debounceRefreshOrderBook(this);
    },
  },
  created() {
    log.info('[ORDER BOOK]: reinit');
  },
  mounted() {
    waitForFirebaseInitialized()
      .then(() => {
        if (process.env.ORDER_BOOK_API_CALL_ENABLED) {
          debounceGetOrderBook(this);
        }
        if (process.env.ORDER_BOOK_FIREBASE_ENABLED) {
          this.updateOrderBookPeriodically(this.contract.contractId);
        }
        this.refreshOrderBook();
      });
  },
  beforeDestroy() {
    if (process.env.ORDER_BOOK_FIREBASE_ENABLED) {
      this.clearOrderBookUpdates(this.contract.contractId);
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/order-book/order-book.js